<template>
    <!-- Send a meeting item to anyone's task manager, a deck you're on, or (mirrorOn set) mirror it onto another meeting's
         board, or (inventory set, SuperForm only) send it to the Inventory App (AddEditMeetingItem.vue) -->
    <div class="send-picker">
        <div v-if="!loaded" class="item-hint">Loading...</div>
        <template v-else>
            <div class="send-picker-kind" role="radiogroup" aria-label="Send to">
                <label :class="kind == 'employee' ? 'active' : ''"><input type="radio" value="employee" v-model="kind" /> A person's tasks</label>
                <label :class="kind == 'board' ? 'active' : ''"><input type="radio" value="board" v-model="kind" :disabled="!decks.length || isPrivate" /> A deck</label>
                <label v-if="mirrorOn" :class="kind == 'meeting' ? 'active' : ''"><input type="radio" value="meeting" v-model="kind" :disabled="!availableMeetings.length" /> A meeting</label>
                <label v-if="inventory" :class="kind == 'inventory' ? 'active' : ''"><input type="radio" value="inventory" v-model="kind" /> Production Projects</label>
            </div>

            <template v-if="kind == 'employee'">
                <select v-model="employeeID" aria-label="Person">
                    <option :value="null">Choose a person</option>
                    <option v-for="e in people" :key="e.ID" :value="e.ID">{{ e.Name }}</option>
                </select>
            </template>
            <template v-else-if="kind == 'inventory'">
                <select v-model="inventoryLocation" aria-label="Location">
                    <option :value="null">Choose a location</option>
                    <option v-for="l in INVENTORY_LOCATIONS" :key="l" :value="l">{{ l }}</option>
                </select>
            </template>
            <template v-else-if="kind == 'meeting'">
                <div class="send-picker-row">
                    <select v-model="meetingID" aria-label="Meeting" @change="meetingColumn = selectedMeeting ? selectedMeeting.Columns[0] : ''">
                        <option :value="null">Choose a meeting</option>
                        <optgroup v-for="g in meetingGroups" :key="g.label" :label="g.label">
                            <option v-for="m in g.meetings" :key="m.ID" :value="m.ID">{{ m.Name }}</option>
                        </optgroup>
                    </select>
                    <select v-if="selectedMeeting" v-model="meetingColumn" aria-label="Column">
                        <option v-for="c in selectedMeeting.Columns" :key="c" :value="c">{{ c }}</option>
                    </select>
                </div>
            </template>
            <template v-else>
                <div class="send-picker-row">
                    <select v-model="slug" aria-label="Deck" @change="column = selectedDeck ? selectedDeck.Columns[0] : ''">
                        <option value="">Choose a deck</option>
                        <option v-for="d in availableDecks" :key="d.Slug" :value="d.Slug">{{ d.Name }}</option>
                    </select>
                    <select v-if="selectedDeck" v-model="column" aria-label="Column">
                        <option v-for="c in selectedDeck.Columns" :key="c" :value="c">{{ c }}</option>
                    </select>
                </div>
            </template>

            <div class="item-hint">{{ reminderHint }}</div>
            <div class="board-item-btns send-picker-btns">
                <div :class="!canSend || busy ? 'disable-button' : ''"><span @click="send()">Send</span></div>
                <div><span @click="$emit('cancel')">Cancel</span></div>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';

// Where an Inventory App card can go (its project board is per location)
const INVENTORY_LOCATIONS = [ 'Cincinnati', 'Pincher Creek' ];

export default defineComponent({
    name: 'SendTaskPicker',
    // sentTo: the item's current cards (meetingitems.ts /sent), so places it's already on aren't offered.
    // mirrorOn: the IDs of the meetings it's already on, to offer A meeting (a mirror); unset where it can't be mirrored.
    // inventory: offer the Inventory App's project board (SuperForm only; API sfprivate.ts)
    props: [ 'assigneeId', 'isPrivate', 'sentTo', 'busy', 'mirrorOn', 'inventory' ],
    emits: [ 'send', 'cancel' ],
    data() {
        return {
            loaded: false,
            kind: 'employee',
            employees: [] as any[],
            decks: [] as any[],
            employeeID: null as any,
            slug: '',
            column: '',
            meetings: [] as any[], // meetings you can open, with their column names (meetingitems.ts /mirror/targets)
            teamOrder: [] as string[], // the meeting groups (teams) in their set order, as in the Agenda quick add
            meetingID: null as any,
            meetingColumn: '',
            // The Inventory App location the card goes on: yours when it's one of them
            INVENTORY_LOCATIONS,
            inventoryLocation: INVENTORY_LOCATIONS.find(l => l.toLowerCase() == String(this.$store.state.user.teamHubEmployee?.Location || '').trim().toLowerCase()) || null as any
        }
    },
    async created() {
        await Promise.all([
            axios.get(`${this.$store.state.apiUrl}/api/taskhandoffs/targets`)
                .then(resp => {
                    this.employees = resp.data.employees;
                    this.decks = resp.data.decks;
                })
                .catch(e => {
                    console.log(e);
                }),
            this.mirrorOn ? axios.get(`${this.$store.state.apiUrl}/api/meetingitems/mirror/targets`)
                .then(resp => {
                    this.meetings = resp.data;
                })
                .catch(e => {
                    console.log(e);
                }) : null,
            this.mirrorOn ? axios.get(`${this.$store.state.apiUrl}/api/meetings/groups`)
                .then(resp => {
                    this.teamOrder = resp.data.map((g: any) => g.name);
                })
                .catch(e => {
                    console.log(e);
                }) : null
        ]);
        this.loaded = true;
    },
    computed: {
        myEmployeeID(): any {
            return this.$store.state.user.employeeID;
        },
        // Anyone (you first) whose task manager it isn't already on
        people(): any[] {
            const onTasks = (this.sentTo || []).filter((s: any) => !s.IsDeck).map((s: any) => s.OwnerEmployeeID);
            const open = this.employees.filter((e: any) => !onTasks.includes(e.ID));
            const me = open.filter((e: any) => e.ID == this.myEmployeeID).map((e: any) => ({ ...e, Name: e.Name + ' (me)' }));
            return [ ...me, ...open.filter((e: any) => e.ID != this.myEmployeeID) ];
        },
        availableDecks(): any[] {
            const onDecks = (this.sentTo || []).filter((s: any) => s.IsDeck).map((s: any) => s.Slug);
            return this.decks.filter((d: any) => d.Columns.length && !onDecks.includes(d.Slug));
        },
        selectedDeck(): any {
            return this.decks.find((d: any) => d.Slug == this.slug) || null;
        },
        // Meetings with a column that it isn't already on
        availableMeetings(): any[] {
            return this.meetings.filter((m: any) => m.Columns.length && !(this.mirrorOn || []).includes(m.ID));
        },
        // One group per team in the teams' set order (as in the Agenda quick add), then My Meetings (personal ones)
        meetingGroups(): any[] {
            const out = [];
            const teams = [ ...new Set(this.availableMeetings.filter((m: any) => m.Team).map((m: any) => m.Team)) ] as string[];
            const rank = (t: string) => this.teamOrder.indexOf(t) >= 0 ? this.teamOrder.indexOf(t) : this.teamOrder.length;
            teams.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
            for(const team of teams) {
                out.push({ label: team, meetings: this.availableMeetings.filter((m: any) => m.Team == team) });
            }
            const mine = this.availableMeetings.filter((m: any) => !m.Team);
            if(mine.length) {
                out.push({ label: 'My Meetings', meetings: mine });
            }
            return out;
        },
        selectedMeeting(): any {
            return this.availableMeetings.find((m: any) => m.ID == this.meetingID) || null;
        },
        canSend(): boolean {
            if(this.kind == 'inventory') {
                return !!this.inventoryLocation;
            }
            if(this.kind == 'meeting') {
                return !!this.selectedMeeting && !!this.meetingColumn;
            }
            return this.kind == 'employee' ? !!this.employeeID : !!this.selectedDeck && !!this.column;
        },
        // Who gets the reminder pop-up (taskHandoffs.ts resolveSendTarget decides the same way)
        reminderHint(): string {
            if(this.isPrivate) {
                return 'Private items can only go on your own task manager.';
            }
            if(this.kind == 'inventory') {
                return 'It goes on the Inventory App\'s project board as a new card. Changes after that aren\'t synced.';
            }
            if(this.kind == 'meeting') {
                return 'It shows on that meeting\'s board too. Edits on either board show on both.';
            }
            if(this.kind == 'employee') {
                if(this.employeeID && this.employeeID == this.myEmployeeID) {
                    return 'It goes on your task manager. Nobody gets a reminder.';
                }
                const person = this.employees.find((e: any) => e.ID == this.employeeID);
                return person ? `It goes on ${person.Name.split(/\s+/)[0]}'s task manager, and they get a reminder until it's done or they say they're not doing it.` : 'They get a reminder until it\'s done or they say they\'re not doing it.';
            }
            if(!this.selectedDeck) {
                return 'It goes on the deck you choose. The item\'s assignee gets a reminder if they\'re on that deck.';
            }
            const assignee = Number(this.assigneeId);
            if(!assignee || assignee == this.myEmployeeID) {
                return 'Nobody gets a reminder: assign the item to someone on this deck first if you want one.';
            }
            const name = (this.employees.find((e: any) => e.ID == assignee)?.Name || 'The assignee').split(/\s+/)[0];
            return this.selectedDeck.MemberEmployeeIDs.includes(assignee)
                ? `${name} (the assignee) gets a reminder until it's done or they say they're not doing it.`
                : `${name} isn't on this deck, so nobody gets a reminder.`;
        }
    },
    methods: {
        send() {
            if(!this.canSend || this.busy) {
                return;
            }
            if(this.kind == 'inventory') {
                this.$emit('send', { Target: 'inventory', Location: this.inventoryLocation });
                return;
            }
            if(this.kind == 'meeting') {
                this.$emit('send', { Target: 'meeting', MeetingID: this.meetingID, Column: this.meetingColumn });
                return;
            }
            // Yourself goes as Send to my tasks
            this.$emit('send', this.kind == 'employee'
                ? (this.employeeID == this.myEmployeeID ? { Target: 'me' } : { Target: 'employee', EmployeeID: this.employeeID })
                : { Target: 'board', Slug: this.slug, Column: this.column });
        }
    }
});
</script>

<style scoped>
.send-picker { border: 1px solid var(--mid-gray); border-radius: 10px; padding: 12px; margin-bottom: 10px; }
.send-picker select { width: 100%; margin: 0 0 8px; }
.send-picker-kind { display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
.send-picker-kind label { display: flex; align-items: center; gap: 6px; border: 1px solid var(--mid-gray); border-radius: 20px; padding: 5px 12px; font-size: 13px; cursor: pointer; }
.send-picker-kind label.active { border-color: var(--primary-color); color: var(--primary-color); font-weight: 700; }
.send-picker-kind input { width: auto; margin: 0; }
.send-picker-row { display: flex; gap: 8px; }
.send-picker-row select { flex: 1; min-width: 0; }
.send-picker-btns { margin: 10px 0 0; }
.send-picker-btns > div { margin-bottom: 0; }
@media(max-width: 600px) { .send-picker-row { flex-direction: column; gap: 0; } }
</style>
